import { useCallback, useEffect, useState, useRef } from 'react';
import { UseDeleteButtonParams, UseDeleteButtonReturn, DeleteButtonPosition } from '../types';

/**
 * 删除按钮位置计算和拖拽状态管理 Hook
 * 
 * 功能：
 * 1. 计算边线和节点删除按钮的位置
 * 2. 管理拖拽状态，拖拽时隐藏删除按钮
 * 3. 处理删除按钮点击事件
 */
export const useDeleteButton = ({
  lfRef,
  canvasRef,
  selectedEdgeId,
  selectedNodeId,
  selectedEdgeIdRef,
  selectedNodeIdRef,
  deleteEdgeWithApi,
  deleteNodeWithApi,
  setSelectedEdgeId,
}: UseDeleteButtonParams): UseDeleteButtonReturn => {
  const [edgeDeleteBtnPos, setEdgeDeleteBtnPos] = useState<DeleteButtonPosition | null>(null);
  const [nodeDeleteBtnPos, setNodeDeleteBtnPos] = useState<DeleteButtonPosition | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  /**
   * 计算边线删除按钮位置（边线中点）
   */
  const computeEdgeDeleteBtnPos = useCallback((edgeId: string | null) => {
    if (!edgeId || !lfRef.current) {
      setEdgeDeleteBtnPos(null);
      return;
    }
    
    try {
      const edge = lfRef.current.getEdgeDataById(edgeId) as any;
      if (!edge?.startPoint || !edge?.endPoint) {
        setEdgeDeleteBtnPos(null);
        return;
      }
      
      const transform = lfRef.current.getTransform() as any;
      const SCALE_X = transform?.SCALE_X || 1;
      const SCALE_Y = transform?.SCALE_Y || 1;
      const TRANSLATE_X = transform?.TRANSLATE_X || 0;
      const TRANSLATE_Y = transform?.TRANSLATE_Y || 0;
      
      const midX = (edge.startPoint.x + edge.endPoint.x) / 2;
      const midY = (edge.startPoint.y + edge.endPoint.y) / 2;
      
      setEdgeDeleteBtnPos({
        left: midX * SCALE_X + TRANSLATE_X,
        top: midY * SCALE_Y + TRANSLATE_Y,
      });
    } catch (error) {
      console.error('计算边线删除按钮位置失败:', error);
      setEdgeDeleteBtnPos(null);
    }
  }, [lfRef]);

  /**
   * 计算节点删除按钮位置（节点右上角）
   */
  const computeNodeDeleteBtnPos = useCallback((nodeId: string | null) => {
    if (!nodeId || !lfRef.current) {
      setNodeDeleteBtnPos(null);
      return;
    }
    
    try {
      const nodeModel = (lfRef.current as any).graphModel?.getNodeModelById(nodeId);
      if (!nodeModel) {
        setNodeDeleteBtnPos(null);
        return;
      }
      
      const transform = lfRef.current.getTransform() as any;
      const SCALE_X = transform?.SCALE_X || 1;
      const SCALE_Y = transform?.SCALE_Y || 1;
      const TRANSLATE_X = transform?.TRANSLATE_X || 0;
      const TRANSLATE_Y = transform?.TRANSLATE_Y || 0;
      
      const { x, y, width, height } = nodeModel;
      
      setNodeDeleteBtnPos({
        left: (x + width / 2) * SCALE_X + TRANSLATE_X - 20,
        top: (y - height / 2) * SCALE_Y + TRANSLATE_Y + 20,
      });
    } catch (error) {
      console.error('计算节点删除按钮位置失败:', error);
      setNodeDeleteBtnPos(null);
    }
  }, [lfRef]);

  /**
   * 边线删除按钮点击处理
   */
  const handleEdgeDeleteBtnClick = useCallback(() => {
    if (!selectedEdgeId) return;
    deleteEdgeWithApi?.(selectedEdgeId);
    setSelectedEdgeId(null);
  }, [deleteEdgeWithApi, selectedEdgeId, setSelectedEdgeId]);

  /**
   * 节点删除按钮点击处理
   */
  const handleNodeDeleteBtnClick = useCallback(() => {
    if (!selectedNodeId) return;
    deleteNodeWithApi?.(selectedNodeId);
    setNodeDeleteBtnPos(null);
  }, [deleteNodeWithApi, selectedNodeId]);

  /**
   * 监听选中边线变化，更新删除按钮位置
   */
  useEffect(() => {
    if (isDragging) {
      setEdgeDeleteBtnPos(null);
      return;
    }
    computeEdgeDeleteBtnPos(selectedEdgeId);
  }, [computeEdgeDeleteBtnPos, isDragging, selectedEdgeId]);

  /**
   * 监听选中节点变化，更新删除按钮位置
   */
  useEffect(() => {
    if (isDragging) {
      setNodeDeleteBtnPos(null);
      return;
    }
    computeNodeDeleteBtnPos(selectedNodeId);
  }, [computeNodeDeleteBtnPos, isDragging, selectedNodeId]);

  /**
   * 监听 LogicFlow 拖拽事件，拖拽时隐藏删除按钮
   */
  useEffect(() => {
    const lf = lfRef.current;
    if (!lf) return;

    const handleDragStart = () => {
      setIsDragging(true);
      setEdgeDeleteBtnPos(null);
      setNodeDeleteBtnPos(null);
    };

    const handleDragEnd = () => {
      setIsDragging(false);
      computeEdgeDeleteBtnPos(selectedEdgeIdRef.current);
      computeNodeDeleteBtnPos(selectedNodeIdRef.current);
    };

    lf.on('node:dragstart', handleDragStart);
    lf.on('node:drop', handleDragEnd);
    lf.on('edge:dragstart' as any, handleDragStart);
    lf.on('edge:drop' as any, handleDragEnd);
    lf.on('edge:adjust' as any, handleDragStart);
    lf.on('edge:adjust-end' as any, handleDragEnd);

    return () => {
      lf.off('node:dragstart', handleDragStart);
      lf.off('node:drop', handleDragEnd);
      lf.off('edge:dragstart' as any, handleDragStart);
      lf.off('edge:drop' as any, handleDragEnd);
      lf.off('edge:adjust' as any, handleDragStart);
      lf.off('edge:adjust-end' as any, handleDragEnd);
    };
  }, [computeEdgeDeleteBtnPos, computeNodeDeleteBtnPos, lfRef, selectedEdgeIdRef, selectedNodeIdRef]);

  /**
   * 兜底：使用 DOM pointer 事件检测拖拽（某些版本 LogicFlow 不会抛出拖拽事件）
   */
  useEffect(() => {
    const container = canvasRef.current;
    if (!container) return;

    const pointerDownRef = { down: false, startX: 0, startY: 0, dragStarted: false };
    const DRAG_THRESHOLD_PX = 2;

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      if (!selectedNodeIdRef.current && !selectedEdgeIdRef.current) return;
      
      pointerDownRef.down = true;
      pointerDownRef.dragStarted = false;
      pointerDownRef.startX = e.clientX;
      pointerDownRef.startY = e.clientY;
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!pointerDownRef.down) return;
      if (pointerDownRef.dragStarted) return;
      
      const dx = Math.abs(e.clientX - pointerDownRef.startX);
      const dy = Math.abs(e.clientY - pointerDownRef.startY);
      if (dx + dy < DRAG_THRESHOLD_PX) return;

      pointerDownRef.dragStarted = true;
      setIsDragging(true);
      setEdgeDeleteBtnPos(null);
      setNodeDeleteBtnPos(null);
    };

    const onPointerUpOrCancel = () => {
      if (!pointerDownRef.down) return;
      const wasDragging = pointerDownRef.dragStarted;
      pointerDownRef.down = false;
      pointerDownRef.dragStarted = false;
      if (!wasDragging) return;

      setIsDragging(false);
      computeEdgeDeleteBtnPos(selectedEdgeIdRef.current);
      computeNodeDeleteBtnPos(selectedNodeIdRef.current);
    };

    container.addEventListener('pointerdown', onPointerDown);
    container.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUpOrCancel);
    window.addEventListener('pointercancel', onPointerUpOrCancel);

    return () => {
      container.removeEventListener('pointerdown', onPointerDown);
      container.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUpOrCancel);
      window.removeEventListener('pointercancel', onPointerUpOrCancel);
    };
  }, [canvasRef, computeEdgeDeleteBtnPos, computeNodeDeleteBtnPos, selectedEdgeIdRef, selectedNodeIdRef]);

  return {
    edgeDeleteBtnPos,
    nodeDeleteBtnPos,
    handleEdgeDeleteBtnClick,
    handleNodeDeleteBtnClick,
  };
};
